import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  ActionItem,
  Box,
  Button,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import Toggler from 'modules/core/components/Toggler'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { automationTranslations } from 'modules/automations/lib/automationTranslations'
import { accessGroups } from 'modules/automations/store/collections/accessGroups'
import { actions } from 'modules/automations/store/collections/actions'
import { automations } from 'modules/automations/store/collections/automations'
import type { Automation } from 'modules/automations/store/models/Automation'

import styles from './index.module.scss'

const cx = classNames.bind(styles)
interface Props {
  automation: Automation
}

export const AutomationListItem = observer(({ automation }: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const [open, toggle] = useToggle()
  const [tooltipOpen, toggleTooltip] = useToggle()
  const label = automation.attributes.name
  const goTo = navigation.settings.automations.id.details.fullPath({ id: automation.id })

  const onDuplicate = async () => {
    const { value, success } = await asyncAction(() => automation.rpc('duplicate'))

    if (success) {
      // Update the collections related to the automation
      await Promise.all([accessGroups.fetch(), actions.fetch(), automations.fetch()])

      navigate(navigation.settings.automations.id.details.fullPath({ id: value.id }))
    }
  }

  const getOptions = (): Option[] => {
    const options: Option[] = [
      {
        to: navigation.settings.automations.id.details.rename.fullPath({ id: automation.id }),
        title: i18n.t('automations.automation.rename.title'),
      },
      {
        to: navigation.settings.automations.id.details.delete.fullPath({ id: automation.id }),
        title: i18n.t('automations.automation.delete.title'),
        type: 'wrong',
      },
    ]

    return [
      {
        onClick: onDuplicate,
        title: i18n.t('automations.automation.duplicate.title'),
      },
      ...(!automation.isDefault ? options : []),
    ]
  }

  const onStopPropagation = (ev: React.SyntheticEvent<unknown>) => {
    ev.stopPropagation()
    ev.preventDefault()
  }

  return (
    <ActionItem to={goTo} showIcon={false}>
      <div className={cx(styles.item)}>
        <div className={cx(styles.left)}>
          <Text.Normal weight="semibold">{label}</Text.Normal>
          <Box flexDirection="row" flexWrap="wrap">
            <Box paddingBottom="s4">
              <Text.Normal size="200">{automationTranslations(automation)}</Text.Normal>
            </Box>
            {automation.attributes.conflict_keys.length > 0 && (
              <Tooltip.Dialog
                open={tooltipOpen}
                toggle={toggleTooltip}
                attachment="bottom center"
                label={i18n.t('automations.conflict_tag_tooltip')}
              >
                <Tag type="champagne"> {i18n.t('automations.conflict_tag')} </Tag>
              </Tooltip.Dialog>
            )}
          </Box>
        </div>
        <div onClick={onStopPropagation} className={styles.right}>
          <div onClick={onStopPropagation} className={cx(styles.toggler)}>
            <Toggler
              active={automation.attributes.enabled}
              type="correct"
              onChange={() => automation.toggleActivation()}
            />
          </div>
          <Popover
            attachment="top right"
            targetAttachment="bottom center"
            toggle={toggle}
            open={open}
            withPreventDefault
          >
            <Button onClick={toggle(true)} type="secondary" iconName="ellipsis" />
            <PopoverContent onClose={toggle(false)} options={getOptions()} />
          </Popover>
        </div>
      </div>
    </ActionItem>
  )
})
